<template>
    <section class="apr-carousel-wrapper">
        <div class="container px-4 xl:px-0 mx-auto">
            <component
                v-if="isTitleAsComponent"
                :is="titleBlockComponent.component || 'div'"
                :blok="titleBlockComponent"
            />
            <component :is="titleTag" v-else-if="blok[sectionConfig.title]" class="apr-section-title">
                {{ blok[sectionConfig.title] }}
            </component>
            <template v-if="blok[sectionConfig.subtitle?.subtitleName]">
                <div
                    v-if="sectionConfig.subtitle.subtitleType === 'text'"
                    class="apr-section-subtitle apr-rich-text"
                    v-html="blok[sectionConfig?.subtitle?.subtitleName]"
                ></div>
                <div
                    v-else
                    class="apr-section-subtitle apr-rich-text"
                    v-html="renderRichText(blok[sectionConfig?.subtitle?.subtitleName])"
                ></div>
            </template>
            <AprCarousel v-if="productList?.length" :slider-items="productList" :slider-options="sliderOptions">
                <template #default="{ item }">
                    <Component
                        :is="getCardType()"
                        v-if="item"
                        :blok="blok"
                        :product-data="item"
                        :airport-data="airport"
                    ></Component>
                </template>
            </AprCarousel>
            <div v-if="!productList?.length" class="no-products">
                Sorry, we do not have any {{ getProductTypeSimple() }} available.
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import AprCarousel from './AprCarousel.vue';
import ParkingProductCard from '../ProductsCards/ParkingProductCard.vue';
import HotelProductCard from '../ProductsCards/HotelProductCard.vue';
import ParkingLotProductCard from '../ProductsCards/ParkingLotProductCard.vue';
import HotelPopularCard from '../cards/hotels-and-airports/HotelPopularCard.vue';
import PackagesDealCard from '../ProductsCards/PackagesDealCard.vue';

import type { IProductsSectionConfig } from '~/../types/ProductsCarousel';

const components = {
    ParkingProductCard,
    HotelProductCard,
    ParkingLotProductCard,
    HotelPopularCard,
    PackagesDealCard,
};

const { blok, airport, products, sectionConfig } = defineProps<{
    blok: any;
    airport?: any;
    products: any;
    sectionConfig: IProductsSectionConfig;
}>();

const sliderOptions = ref({
    slidesCount: {
        xl: 3,
        lg: 3,
        md: 2,
        sm: 1,
    },
});

const productList: any = computed(() => {
    if (blok.product_type === 'HotelWithParking' || blok.product_type === 'Parking') {
        return filterProducts(blok, products);
    }
    return products;
});

function getCardType() {
    if (sectionConfig.cardType !== 'HotelOrParking') return components[sectionConfig.cardType];
    if (blok.product_type === 'HotelWithParking') {
        return components['HotelProductCard'];
    } else if (blok.product_type === 'Parking') {
        return components['ParkingProductCard'];
    }
}

function getProductTypeSimple() {
    if (blok.product_type === 'HotelWithParking') {
        return 'hotels';
    } else if (blok.product_type === 'Parking') {
        return 'parking lots';
    }
}

const titleTag = computed(() => (sectionConfig.titleTag ? sectionConfig.titleTag : 'h3'));

const titleBlockComponent = computed(() => blok.titleComponent?.[0] ?? null);

const isTitleAsComponent = computed<boolean>(() => blok.headingType === 'component' && !!titleBlockComponent.value);
</script>

<style lang="postcss" scoped>
.apr-section-title + :deep(.apr-carousel) {
    @apply mt-6 lg:mt-8;
}

.apr-carousel-wrapper {
    @apply apr-section;
    :deep(.carousel) {
        @apply -mx-3;
        .carousel__slide {
            @apply pb-6;
        }
        .carousel__pagination {
            @apply -bottom-2;
        }
    }
}

:deep(.carousel) {
    .carousel__slide {
        .apr-product-card {
            @apply w-full h-full;
        }
    }
    &.is-less-items {
        .carousel__track {
            @apply justify-start;
        }
    }
}
</style>
